// The ways a diagram can be arranged, and the layouts that need no layout engine (grid, radial).
// Pure and testable; layout.ts runs ELK for the others.

export type LayoutKind = 'right' | 'left' | 'down' | 'up' | 'organic' | 'radial' | 'grid' | 'namespace' | 'schema';

/** The layouts that flow in one direction, from referenced tables to the tables that point at them. */
export type FlowKind = 'right' | 'left' | 'down' | 'up';

export const isFlow = (kind: LayoutKind): kind is FlowKind => kind === 'right' || kind === 'left' || kind === 'down' || kind === 'up';

export interface LayoutInfo {
  id: LayoutKind;
  label: string;
  description: string;
}

export const LAYOUTS: LayoutInfo[] = [
  { id: 'right', label: 'Left to right', description: 'Referenced tables on the left, the tables that point at them on the right.' },
  { id: 'left', label: 'Right to left', description: 'Referenced tables on the right, the tables that point at them on the left.' },
  { id: 'down', label: 'Top down', description: 'Referenced tables at the top, the tables that point at them below.' },
  { id: 'up', label: 'Bottom up', description: 'Referenced tables at the bottom, the tables that point at them above.' },
  { id: 'organic', label: 'Organic', description: 'Related tables pulled close together, so clusters stand out.' },
  { id: 'radial', label: 'Radial', description: 'Rings around the most connected table, one ring per step away.' },
  { id: 'grid', label: 'Grid A–Z', description: 'Rows in alphabetical order, ignoring relationships. Easy to scan.' },
  { id: 'namespace', label: 'By namespace', description: 'A frame per C# namespace, laid out left to right inside.' },
  { id: 'schema', label: 'By schema', description: 'A frame per database schema, laid out left to right inside.' },
];

/** How far apart cards are. Every setting also grows with the number of cards, so big models get room for their lines. */
export type Spacing = 'compact' | 'normal' | 'roomy';

export const SPACINGS: { id: Spacing; label: string; description: string }[] = [
  { id: 'compact', label: 'Compact', description: 'Cards close together.' },
  { id: 'normal', label: 'Normal', description: 'More room as the model grows.' },
  { id: 'roomy', label: 'Roomy', description: 'Wide gaps, for models with many lines.' },
];

/** Multipliers for the gaps beside cards (`nodes`) and between columns or rings (`layers`), where lines run. */
export interface SpacingScale {
  nodes: number;
  layers: number;
}

export function spacingScale(spacing: Spacing, cards: number): SpacingScale {
  // From 15 cards up to 75, gaps grow; lines between columns need the most room.
  const crowd = Math.min(1, Math.max(0, (cards - 15) / 60));
  const base = { compact: 0.7, normal: 1, roomy: 1.6 }[spacing];
  return { nodes: base * (1 + 0.5 * crowd), layers: base * (1 + 1.2 * crowd) };
}

export const isGrouped = (kind: LayoutKind): kind is 'namespace' | 'schema' => kind === 'namespace' || kind === 'schema';

/** Where a layout's card positions are kept. Left to right with normal spacing keeps the key used before there was a choice. */
export function layoutStorageKey(base: string, kind: LayoutKind, spacing: Spacing = 'normal'): string {
  return `${base}${kind === 'right' ? '' : `|${kind}`}${spacing === 'normal' ? '' : `|${spacing}`}`;
}

export interface LayoutNode {
  id: string;
  width: number;
  height: number;
  /** For grouped layouts. */
  namespace?: string | null;
  schema?: string | null;
}

export interface LayoutEdge {
  id: string;
  /** Layout direction: parents (principals, base types) first. */
  from: string;
  to: string;
}

export type Positions = Record<string, { x: number; y: number }>;

/**
 * The frame each node belongs to in a grouped layout. Nodes without a namespace (shared-type join
 * entities) or schema join the group of a related node, so they sit next to what they connect.
 */
export function assignGroups(nodes: LayoutNode[], edges: LayoutEdge[], kind: 'namespace' | 'schema'): Map<string, string> {
  const own = new Map(nodes.map((n) => [n.id, (kind === 'namespace' ? n.namespace : n.schema) ?? null]));
  const out = new Map<string, string>();
  for (const [id, g] of own) if (g) out.set(id, g);
  for (const n of nodes) {
    if (out.has(n.id)) continue;
    const neighbor = edges.map((e) => (e.from === n.id ? e.to : e.to === n.id ? e.from : null)).find((x) => x && own.get(x));
    out.set(n.id, (neighbor && own.get(neighbor)) || (kind === 'namespace' ? 'No namespace' : 'Default schema'));
  }
  return out;
}

/** Shorter group labels: the part after the prefix all namespaces share (`Shop.Catalog`, `Shop.Orders` → `Catalog`, `Orders`). */
export function groupLabels(groups: string[]): Map<string, string> {
  const dotted = groups.filter((g) => g.includes('.'));
  let prefix = '';
  if (dotted.length === groups.length && groups.length > 1) {
    const parts = groups.map((g) => g.split('.'));
    const common: string[] = [];
    for (let i = 0; parts.every((p) => p.length > i + 1 && p[i] === parts[0]![i]); i++) common.push(parts[0]![i]!);
    if (common.length) prefix = `${common.join('.')}.`;
  }
  return new Map(groups.map((g) => [g, prefix ? g.slice(prefix.length) : g]));
}

/** Cards in rows, sorted by name, about 1.6 times as wide as tall. */
export function gridLayout(nodes: LayoutNode[], scale = 1): Positions {
  const gap = { x: 48 * scale, y: 56 * scale };
  const sorted = [...nodes].sort((a, b) => a.id.localeCompare(b.id, undefined, { sensitivity: 'base' }));
  const area = sorted.reduce((sum, n) => sum + (n.width + gap.x) * (n.height + gap.y), 0);
  const rowWidth = Math.max(Math.sqrt(area * 1.6), ...sorted.map((n) => n.width));
  const out: Positions = {};
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  for (const n of sorted) {
    if (x > 0 && x + n.width > rowWidth) {
      x = 0;
      y += rowHeight + gap.y;
      rowHeight = 0;
    }
    out[n.id] = { x, y };
    x += n.width + gap.x;
    rowHeight = Math.max(rowHeight, n.height);
  }
  return out;
}

/**
 * Rings around `center` (default: the most connected node): one ring per step away from it, each
 * node near the node that led to it. Nodes not connected to the center are a grid below the rings.
 */
export function radialLayout(nodes: LayoutNode[], edges: LayoutEdge[], center?: string, scale = 1): Positions {
  if (nodes.length === 0) return {};
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const neighbors = new Map<string, string[]>(nodes.map((n) => [n.id, []]));
  for (const e of edges) {
    if (e.from === e.to || !byId.has(e.from) || !byId.has(e.to)) continue;
    neighbors.get(e.from)!.push(e.to);
    neighbors.get(e.to)!.push(e.from);
  }
  const degree = (id: string) => new Set(neighbors.get(id)).size;
  center = center && byId.has(center) ? center : [...nodes].sort((a, b) => degree(b.id) - degree(a.id) || a.id.localeCompare(b.id))[0]!.id;

  // Rings by distance; each node remembers the node it was reached from.
  const rings: string[][] = [[center]];
  const parent = new Map<string, string>();
  const seen = new Set([center]);
  for (let frontier = [center]; frontier.length; ) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const other of [...new Set(neighbors.get(id))].sort()) {
        if (seen.has(other)) continue;
        seen.add(other);
        parent.set(other, id);
        next.push(other);
      }
    }
    if (next.length) rings.push(next);
    frontier = next;
  }

  const out: Positions = {};
  const angle = new Map<string, number>([[center, 0]]);
  const c = byId.get(center)!;
  out[center] = { x: -c.width / 2, y: -c.height / 2 };
  const diagonal = (id: string) => Math.hypot(byId.get(id)!.width, byId.get(id)!.height);
  let radius = 0;
  let previousSize = diagonal(center);
  for (let k = 1; k < rings.length; k++) {
    // Keep children in the order of their parents' angles, so lines cross less.
    const ring = [...rings[k]!].sort((a, b) => angle.get(parent.get(a)!)! - angle.get(parent.get(b)!)! || a.localeCompare(b));
    const size = Math.max(...ring.map(diagonal));
    const slot = (id: string) => diagonal(id) * 0.8 + 40 * scale;
    const circumference = ring.reduce((sum, id) => sum + slot(id), 0);
    radius = Math.max(radius + previousSize / 2 + size / 2 + 60 * scale, circumference / (2 * Math.PI));
    previousSize = size;
    // The first ring is spread evenly. Outer rings pack their cards next to the card each hangs from,
    // as close to it as the cards beside them allow; a nearly full ring is spread evenly and turned.
    const widths = ring.map((id) => slot(id) / radius);
    const total = widths.reduce((sum, w) => sum + w, 0);
    const wanted = ring.map((id) => (k === 1 ? 0 : angle.get(parent.get(id)!)!));
    let angles: number[] = [];
    if (k > 1 && total < 1.6 * Math.PI) {
      for (let i = 0; i < ring.length; i++) angles.push(i === 0 ? wanted[0]! : Math.max(wanted[i]!, angles[i - 1]! + (widths[i - 1]! + widths[i]!) / 2));
      const shift = angles.reduce((sum, a, i) => sum + a - wanted[i]!, 0) / ring.length;
      angles = angles.map((a) => a - shift);
    } else {
      let at = 0;
      for (const w of widths) {
        angles.push(at + ((w / total) * 2 * Math.PI) / 2);
        at += (w / total) * 2 * Math.PI;
      }
      const turn =
        k === 1
          ? -Math.PI / 2
          : Math.atan2(
              wanted.reduce((sum, w, i) => sum + Math.sin(w - angles[i]!), 0),
              wanted.reduce((sum, w, i) => sum + Math.cos(w - angles[i]!), 0),
            );
      angles = angles.map((a) => a + turn);
    }
    ring.forEach((id, i) => {
      const a = angles[i]!;
      angle.set(id, a);
      const n = byId.get(id)!;
      out[id] = { x: radius * Math.cos(a) - n.width / 2, y: radius * Math.sin(a) - n.height / 2 };
    });
  }

  const rest = nodes.filter((n) => !seen.has(n.id));
  if (rest.length) {
    const bottom = Math.max(...Object.entries(out).map(([id, p]) => p.y + byId.get(id)!.height));
    const left = Math.min(...Object.values(out).map((p) => p.x));
    for (const [id, p] of Object.entries(gridLayout(rest, scale))) out[id] = { x: p.x + left, y: p.y + bottom + 120 * scale };
  }
  return out;
}

/** Pushes overlapping cards apart (organic layouts place centers, not boxes). */
export function removeOverlaps(nodes: LayoutNode[], positions: Positions, gap = 32): Positions {
  const out: Positions = Object.fromEntries(nodes.filter((n) => positions[n.id]).map((n) => [n.id, { ...positions[n.id]! }]));
  const list = nodes.filter((n) => out[n.id]);
  for (let round = 0; round < 80; round++) {
    let moved = false;
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]!;
        const b = list[j]!;
        const pa = out[a.id]!;
        const pb = out[b.id]!;
        const dx = pb.x + b.width / 2 - (pa.x + a.width / 2);
        const dy = pb.y + b.height / 2 - (pa.y + a.height / 2);
        const ox = (a.width + b.width) / 2 + gap - Math.abs(dx);
        const oy = (a.height + b.height) / 2 + gap - Math.abs(dy);
        if (ox <= 0 || oy <= 0) continue;
        moved = true;
        // Move apart along the axis that needs the smaller push.
        if (ox < oy) {
          const s = (dx >= 0 ? 1 : -1) * (ox / 2 + 0.5);
          pa.x -= s;
          pb.x += s;
        } else {
          const s = (dy >= 0 ? 1 : -1) * (oy / 2 + 0.5);
          pa.y -= s;
          pb.y += s;
        }
      }
    }
    if (!moved) break;
  }
  return out;
}
